import { useState, useEffect } from 'react';
import { useGameStore, useJwt, useAuthHeaders, EventBus } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Modal, Tabs, Button, Input, Badge } from '../ds/index.js';
import { WebhooksPanel } from './WebhooksPanel.js';

interface TenantApp {
  id: string;
  name: string;
  type: 'iframe' | 'launch';
  url: string;
  iconUrl: string | null;
  isActive: boolean;
  isSystem?: boolean;
  position: number;
}

// ── Icones Microsoft (SVG inline) ───────────────────────────

function TeamsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M16.5 3A2.5 2.5 0 0119 5.5V7h1.5A2.5 2.5 0 0123 9.5v5a2.5 2.5 0 01-2.5 2.5H19v1.5a2.5 2.5 0 01-2.5 2.5h-9A2.5 2.5 0 015 18.5V17H3.5A2.5 2.5 0 011 14.5v-5A2.5 2.5 0 013.5 7H5V5.5A2.5 2.5 0 017.5 3h9z" fill="#5059C9"/>
      <circle cx="17" cy="5" r="2" fill="#7B83EB"/>
      <circle cx="12" cy="7" r="3" fill="#fff"/>
      <rect x="4" y="10" width="10" height="8" rx="1" fill="#fff" fillOpacity="0.9"/>
    </svg>
  );
}

function OutlookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M22 6.5V17a2 2 0 01-2 2H9V5h11a2 2 0 012 2v-.5z" fill="#0078D4"/>
      <path d="M2 7l7-3v16l-7-3V7z" fill="#0364B8"/>
      <ellipse cx="5.5" cy="12" rx="2.5" ry="3" fill="#fff"/>
    </svg>
  );
}

function getSystemIcon(id: string): React.ReactNode | null {
  if (id === 'system:teams') return <TeamsIcon />;
  if (id === 'system:outlook') return <OutlookIcon />;
  return null;
}

function getTenantIdFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.tid as string) ?? '';
  } catch { return ''; }
}

function decodeJwt(jwt: string | null): { role: string; isAdmin: boolean } | null {
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    const role = (payload.role ?? 'member') as string;
    return { role, isAdmin: ['admin', 'owner'].includes(role.toLowerCase()) };
  } catch { return null; }
}

const APP_URL_ERRORS: Record<string, string> = {
  HTTPS_REQUIRED: 'Pour l\u2019afficher dans le campus, l\u2019adresse doit commencer par https://.',
  INVALID_URL: 'Cette adresse n\u2019est pas valide.',
  BLOCKED_URL: 'Cette adresse ne peut pas être affichée dans le campus (adresse interne). Choisis « Nouvel onglet ».',
  INVALID_PORT: 'Cette adresse utilise un port non autorisé dans le campus. Choisis « Nouvel onglet ».',
  MISSING_FIELDS: 'Renseigne le nom et l\u2019adresse.',
};

/**
 * AppsModal — les apps de l'espace (tous les membres), ajout d'app et onglet
 * Webhooks réservés aux ADMIN/OWNER. Sur mobile : liste seule, l'ajout et les
 * webhooks se font depuis un ordinateur.
 */
export function AppsModal({ onClose, initialTab = 'apps' }: { onClose: () => void; initialTab?: 'apps' | 'webhooks' }) {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);
  const jwtInfo = decodeJwt(jwt);
  const isAdmin = jwtInfo?.isAdmin ?? false;
  const isMobile = useIsMobile();
  const canManage = isAdmin && !isMobile;
  const [tab, setTab] = useState<'apps' | 'webhooks'>(canManage ? initialTab : 'apps');

  const [apps, setApps] = useState<TenantApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Formulaire d'ajout (admin)
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState<'iframe' | 'launch'>('iframe');
  const [formUrl, setFormUrl] = useState('');
  const [formIconUrl, setFormIconUrl] = useState('');
  const [formPosition, setFormPosition] = useState(0);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    try { EventBus.emit('ui:modal-opened', {}); } catch {}
    return () => { try { EventBus.emit('ui:modal-closed', {}); } catch {} };
  }, []);

  useEffect(() => {
    if (!jwt || !tenantId) { setLoading(false); return; }
    let cancelled = false;
    fetch(`${API_BASE}/integration/apps`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data as TenantApp[] : [];
        setApps(list);
        setFormPosition(list.length);
      })
      .catch(() => { if (!cancelled) setError("Impossible de charger les apps. Réessaie dans un instant."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jwt, tenantId]);

  async function handleCreate() {
    if (!jwt || !formName.trim() || !formUrl.trim()) return;
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${API_BASE}/integration/apps`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formName.trim(),
          type: formType,
          url: formUrl.trim(),
          iconUrl: formIconUrl.trim() || undefined,
          position: formPosition,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string };
        setError(APP_URL_ERRORS[err.error ?? ''] ?? "L'app n'a pas pu être ajoutée. Réessaie dans un instant.");
        return;
      }
      const { notice: appNotice, ...app } = await res.json() as TenantApp & { notice?: string };
      setApps((prev) => [...prev, app].sort((a, b) => a.position - b.position));
      if (appNotice === 'EMBED_REFUSED') {
        setNotice(`${app.name} refuse d'être affiché dans le campus : l'app s'ouvrira dans un nouvel onglet.`);
      } else if (appNotice === 'EMBED_UNCHECKED') {
        setNotice(`Impossible de vérifier que ${app.name} accepte d'être affiché dans le campus. Ouvre-la pour t'en assurer ; si la fenêtre reste vide, choisis plutôt « Nouvel onglet ».`);
      }
      setFormName(''); setFormUrl(''); setFormIconUrl(''); setFormPosition((n) => n + 1);
      setShowForm(false);
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(app: TenantApp) {
    if (!jwt || !window.confirm(`Retirer « ${app.name} » de l'espace ?`)) return;
    try {
      const res = await fetch(`${API_BASE}/integration/apps/${app.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (res.ok || res.status === 204) {
        setApps((prev) => prev.filter((a) => a.id !== app.id));
      }
    } catch { /* ignore */ }
  }

  function handleAppClick(app: TenantApp) {
    if (app.type === 'launch') {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    } else {
      // iframe → ouvrir via le systeme activeArea existant
      useGameStore.getState().setActiveArea({
        name: app.name,
        url: app.url,
        label: app.name,
        action: 'iframe',
      });
      useGameStore.getState().setIframeDismissed(false);
      onClose();
    }
  }

  const appsTab = (
    <>
      <p className="pl-lead">Les outils de ton espace. Ouvre-les dans un nouvel onglet ou directement dans le campus.</p>
      {error && <div className="pl-alert" role="alert">{error}</div>}
      {notice && (
        <div className="pl-alert pl-alert-warning" role="status" style={{ justifyContent: 'space-between' }}>
          <span>{notice}</span>
          <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setNotice(null)} />
        </div>
      )}

      {loading ? (
        <p className="pl-lead">Chargement…</p>
      ) : apps.length === 0 ? (
        <p className="pl-lead" style={{ color: 'var(--color-text-muted)' }}>
          Aucune app pour l'instant.{canManage ? ' Ajoute la première ci-dessous.' : ''}
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {apps.map((app) => {
            const sysIcon = app.isSystem ? getSystemIcon(app.id) : null;
            return (
              <li key={app.id} className="pl-card">
                <div className="pl-app-tile" aria-hidden="true" style={sysIcon ? { background: 'var(--color-bg-raised)' } : undefined}>
                  {sysIcon ?? (app.iconUrl
                    ? <img src={app.iconUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : app.name.charAt(0).toUpperCase())}
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 15, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.name}</span>
                  <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {app.isSystem && <Badge>Microsoft</Badge>}
                    {app.type === 'iframe' ? <Badge tone="primary">Dans le campus</Badge> : <Badge>Nouvel onglet</Badge>}
                  </span>
                </div>
                <Button variant="secondary" size="sm" onClick={() => handleAppClick(app)}>Ouvrir</Button>
                {canManage && !app.isSystem && (
                  <Button variant="ghost" size="sm" icon="x" aria-label={`Retirer ${app.name} de l'espace`} onClick={() => { void handleDelete(app); }} />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canManage && !showForm && (
        <div><Button variant="secondary" icon="plus" onClick={() => setShowForm(true)}>Ajouter une app</Button></div>
      )}

      {canManage && showForm && (
        <section aria-label="Ajouter une app" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16, borderRadius: 'var(--radius-lg)', border: '1px dashed var(--color-border-control)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className="pl-section-title">Ajouter une app</h3>
            <Badge mono>Admin</Badge>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <Input label="Nom" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Tableau blanc" />
            <Input label="Adresse (URL)" type="url" value={formUrl} onChange={(e) => setFormUrl(e.target.value)} placeholder="https://…" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="pl-field-label">Ouverture</span>
            <div><Tabs aria-label="Ouverture" items={[{ id: 'iframe', label: 'Dans le campus' }, { id: 'launch', label: 'Nouvel onglet' }]} value={formType} onChange={setFormType} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 1fr)', gap: 12 }}>
            <Input label="Icône (URL, facultatif)" type="url" value={formIconUrl} onChange={(e) => setFormIconUrl(e.target.value)} placeholder="https://…/icone.png" />
            <Input label="Position" type="number" min={0} value={formPosition} onChange={(e) => setFormPosition(parseInt(e.target.value) || 0)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Annuler</Button>
            <Button variant="primary" onClick={() => { void handleCreate(); }} disabled={creating || !formName.trim() || !formUrl.trim()}>
              {creating ? 'Ajout…' : "Ajouter l'app"}
            </Button>
          </div>
        </section>
      )}

      {isAdmin && isMobile && (
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: 'var(--color-text-muted)' }}>
          Ajouter une app ou gérer les webhooks se fait depuis un ordinateur.
        </p>
      )}
    </>
  );

  return (
    <Modal title="Apps" size="lg" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, color: 'var(--color-text-primary)' }}>
        {canManage && (
          <div><Tabs aria-label="Apps ou webhooks" items={[{ id: 'apps', label: 'Apps' }, { id: 'webhooks', label: 'Webhooks' }]} value={tab} onChange={setTab} /></div>
        )}
        {tab === 'apps' ? appsTab : <WebhooksPanel />}
      </div>
    </Modal>
  );
}
